import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCoaches, setCurrentPage, setSelectedCoach, addCoach, updateCoach, deleteCoach } from '../../redux/coaches/coachSlice';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css'; // Import Toastr CSS

const Coaches = () => {
  const dispatch = useDispatch();
  
  const { coaches, total, totalPages, currentPage, loading, error, selectedCoach } = useSelector(
    (state) => state.coaches
  );

  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false); // Add coach modal
  const [showEditModal, setShowEditModal] = useState(false); // Edit coach modal
  const [showViewModal, setShowViewModal] = useState(false); // View coach modal
  const [newCoach, setNewCoach] = useState({
     firstname: '',
     middlename: '',
     lastname: '',
     title: '',
     email:'',
     mobile:'',
     parentName:'',
     relationship:'',
     motherTongue:'',
     gender:'',
     dob:'',
     address:'',
     state:'',
     district:'',
     pincode:'',
     fideId:'',
     aicfId:'',
     tnscaId:''
  });

  const coachesPerPage = 5;
  const maxPageNumbers = 5;

  useEffect(() => {
    dispatch(fetchCoaches({ search: searchTerm, page: currentPage, limit: coachesPerPage }));
  }, [dispatch, searchTerm, currentPage]);

  const handleSearchChange = (e) => setSearchTerm(e.target.value);

  const handleSearch = () => {
    dispatch(fetchCoaches({ search: searchTerm, page: 1, limit: coachesPerPage })); // Fetch coaches with the search term
  };

  const paginate = (pageNumber) => {
    dispatch(setCurrentPage(pageNumber));
  };

  const openEditModal = (coach) => {
    dispatch(setSelectedCoach(coach)); // Set selected coach for editing
    setShowEditModal(true); // Open the edit modal
  };

  const openViewModal = (coach) => {
    dispatch(setSelectedCoach(coach)); // Set selected coach for viewing
    setShowViewModal(true); // Open the view modal
  };

  const openAddModal = () => {
    setNewCoach({ firstname: '',
      middlename: '',
      lastname: '',
      title: '',
      email:'',
      mobile:'',
      parentName:'',
      relationship:'',
      motherTongue:'',
      gender:'',
      dob:'',
      address:'',
      state:'',
      district:'',
      pincode:'',
      fideId:'',
      aicfId:'',
      tnscaId:'' }); // Clear data for new coach
    setShowAddModal(true); // Open the add modal
  };

  const handleAddCoach = async () => {
    try {
      await dispatch(addCoach(newCoach)); // Dispatch the add coach action
      // After adding, fetch the updated coach list
      dispatch(fetchCoaches({ search: searchTerm, page: currentPage, limit: coachesPerPage }));
      setShowAddModal(false); // Close the modal after adding
      toastr.success('Coach added successfully!'); // Toastr success notification
    } catch (error) {
      console.error("Error adding coach:", error);
      toastr.error('Error adding coach!'); // Toastr error notification
    }
  };

  const handleUpdateCoach = async () => {
    if (selectedCoach) {
      try {
        await dispatch(updateCoach({ id: selectedCoach.id, coachData: selectedCoach })); // Update coach
        // After updating, fetch the updated coach list
        dispatch(fetchCoaches({ search: searchTerm, page: currentPage, limit: coachesPerPage }));
        setShowEditModal(false); // Close the modal after updating
        toastr.success('Coach updated successfully!'); // Toastr success notification
      } catch (error) {
        console.error("Error updating coach:", error);
        toastr.error('Error updating coach!'); // Toastr error notification
      }
    }
  };

  const handleDeleteCoach = (id) => {
    try {
      dispatch(deleteCoach(id)); // Dispatch delete action
      toastr.success('Coach deleted successfully!'); // Toastr success notification
    } catch (error) {
      console.error("Error deleting coach:", error);
      toastr.error('Error deleting coach!'); // Toastr error notification
    }
  };

  const pageNumbers = [];
  const startPage = Math.max(1, currentPage - Math.floor(maxPageNumbers / 2));
  const endPage = Math.min(totalPages, startPage + maxPageNumbers - 1);

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <section className="content">
      <div className="container-fluid">
        <div className="card">
          <div className="card-header d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <h3 className="card-title mb-0 mr-2">Coaches List</h3>
              <button
                className="btn btn-success btn-sm rounded-circle d-flex justify-content-center align-items-center"
                style={{ width: '30px', height: '30px', padding: 0 }}
                onClick={openAddModal}
                title="Add Coach"
              >
                <i className="fas fa-plus"></i>
              </button>
            </div>

            {/* Search Section */}
            <div className="d-flex align-items-center">
              <input
                type="text"
                className="form-control form-control-sm mr-2"
                placeholder="Search Coach"
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </div>
          </div>

          <div className="card-body p-0">
            {loading && (
              <div className="text-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            )}
            {error && <p className="text-center text-danger">Error: {error}</p>}
            <table className="table table-sm table-striped">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Coach Name</th>
                  <th>Email</th>
                  <th>Mobile No</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {coaches.map((coach) => (
                  <tr key={coach.id}>
                    <td>{coach.id}</td>
                    <td>{coach.first_name + ' '+ coach.middle_name + ' '+ coach.last_name}</td>
                    <td>{coach.email}</td>
                    <td>{coach.mobile_no}</td>
                    <td className="project-actions text-center">
                      <button onClick={() => openViewModal(coach)} className="btn btn-primary btn-sm mx-1">
                        <i className="fas fa-folder mr-1"></i> View
                      </button>
                      <button onClick={() => openEditModal(coach)} className="btn btn-info btn-sm mx-1">
                        <i className="fas fa-pencil-alt mr-1"></i> Edit
                      </button>
                      <button onClick={() => handleDeleteCoach(coach.id)} className="btn btn-danger btn-sm mx-1">
                        <i className="fas fa-trash mr-1"></i> Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card-footer clearfix">
            <ul className="pagination pagination-sm m-0 float-right">
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => paginate(1)}>First</button>
              </li>
              <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => paginate(currentPage - 1)}>Previous</button>
              </li>
              {pageNumbers.map((number) => (
                <li key={number} className={`page-item ${currentPage === number ? 'active' : ''}`}>
                  <button className="page-link" onClick={() => paginate(number)}>{number}</button>
                </li>
              ))}
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => paginate(currentPage + 1)}>Next</button>
              </li>
              <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => paginate(totalPages)}>Last</button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Add Coach Modal */}
      {showAddModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="exampleModalLabel">Add Coach</h5>
                <button type="button" className="close" onClick={() => setShowAddModal(false)}>&times;</button>
              </div>
              <div className="modal-body">

              <form className="container">
  {/* Row 1 */}
  <div className="row mb-3">
    <div className="col-md-4">
      <label className="form-label">First Name <small className="text-warning">(Capital Letters Only)*</small></label>
      <input type="text" className="form-control" id="firstname" name="firstname" placeholder="First Name" 
       value={newCoach.firstname}
       onChange={(e) => setNewCoach({ ...newCoach, firstname: e.target.value })}
      />
      <span className="error-message text-danger" />
    </div>
    <div className="col-md-4">
      <label className="form-label">Middle Name</label>
      <input type="text" className="form-control skip-validation" id="middlename" name="middlename" placeholder="Middle Name" 
      value={newCoach.middlename}
      onChange={(e) => setNewCoach({ ...newCoach, middlename: e.target.value })}
      />
      <span className="error-message text-danger" />
    </div>
    <div className="col-md-4">
      <label className="form-label">Last Name <small className="text-warning">(Capital Letters Only)*</small></label>
      <input type="text" className="form-control" id="lastname" name="lastname" placeholder="Last Name"
      value={newCoach.lastname}
      onChange={(e) => setNewCoach({ ...newCoach, lastname: e.target.value })}
      />
      <span className="error-message text-danger" />
    </div>
  </div>

  {/* Row 2 */}
  <div className="row mb-3">
    <div className="col-md-4">
      <label className="form-label">Title <small className="text-danger">*</small></label>
      <input type="text" className="form-control" id="title" name="title" placeholder="Title" 
       value={newCoach.title}
       onChange={(e) => setNewCoach({ ...newCoach, title: e.target.value })}
  />
      <span className="error-message text-danger" id="title-errmsg" />
    </div>
    <div className="col-md-4">
      <label className="form-label">Email <small className="text-danger">*</small></label>
      <input type="email" className="form-control" id="email" name="email" placeholder="Email" 
       value={newCoach.email}
       onChange={(e) => setNewCoach({ ...newCoach, email: e.target.value })}
  />
      <span className="error-message text-danger" id="email-errmsg" />
    </div>
    <div className="col-md-4">
      <label className="form-label">Mobile Number <small className="text-danger">*</small></label>
      <input type="text" className="form-control" id="mobile" name="mobile" maxLength={20} placeholder="Mobile Number" 
       value={newCoach.mobile}
       onChange={(e) => setNewCoach({ ...newCoach, mobile: e.target.value })}
  />
      <span className="error-message text-danger" id="mobile-errmsg" />
    </div>
  </div>

  {/* Row 3 */}
  <div className="row mb-3">
    <div className="col-md-4">
      <label className="form-label">Son/Daughter of <small className="text-danger">*</small></label>
      <input type="text" className="form-control" id="parentName" name="parentName" placeholder="Parent's Name"  
      value={newCoach.parentName}
      onChange={(e) => setNewCoach({ ...newCoach, parentName: e.target.value })}
    />
      <span className="error-message text-danger" id="parentName-errmsg" />
    </div>
    <div className="col-md-4">
      <label className="form-label">Relationship</label>
      <select className="form-control" id="relationship" name="relationship" 
      value={newCoach.relationship || ""}
      onChange={(e) => setNewCoach({ ...newCoach, relationship: e.target.value })}
      >
        <option value="">- - Select Relationship - -</option>
        <option value="Father">Father</option>
        <option value="Mother">Mother</option>
        <option value="Other">Other</option>
      </select>
    </div>
    <div className="col-md-4">
      <label className="form-label">Mother Tongue <small className="text-danger">*</small></label>
      <input type="text" className="form-control" id="motherTongue" name="motherTongue" placeholder="Mother Tongue" 
      value={newCoach.motherTongue}
      onChange={(e) => setNewCoach({ ...newCoach, motherTongue: e.target.value })}
      />
      <span className="error-message text-danger" id="motherTongue-errmsg" />
    </div>
  </div>

  {/* Row 4 */}
  <div className="row mb-3">
    <div className="col-md-4">
      <label className="form-label">Gender</label>
      <select className="form-control" id="gender" name="gender" 
      value={newCoach.gender || ""}
      onChange={(e) => setNewCoach({ ...newCoach, gender: e.target.value })}

      >
        <option value="">- - Select Gender - -</option>
        <option value="Male">Male</option>
        <option value="Female">Female</option>
        <option value="Other">Other</option>
      </select>
    </div>
    <div className="col-md-4">
      <label className="form-label">Date of Birth <small className="text-danger">*</small></label>
      <input type="date" className="form-control" id="dob" name="dob" 
      value={newCoach.dob || ""}
      onChange={(e) => setNewCoach({ ...newCoach, dob: e.target.value })}
       />
      <span className="error-message text-danger" id="dob-errmsg" />
    </div>
    <div className="col-md-4">
      <label className="form-label">Address <small className="text-danger">*</small></label>
      <input type="text" className="form-control" id="address" name="address" placeholder="Address" 
      value={newCoach.address || ""}
      onChange={(e) => setNewCoach({ ...newCoach, address: e.target.value })}
      />
      <span className="error-message text-danger" id="address-errmsg" />
    </div>
  </div>

  {/* Row 5 */}
  <div className="row mb-3">
    <div className="col-md-4">
      <label className="form-label">State</label>
      <select className="form-control" id="state" name="state"
      value={newCoach.state || ""}
      onChange={(e) => setNewCoach({ ...newCoach, state: e.target.value })}

      >
        <option value="">- - Select State - -</option>
        <option value="Tamil Nadu">Tamil Nadu</option>
        <option value="Kerala">Kerala</option>
        <option value="Karnataka">Karnataka</option>
      </select>
    </div>
    <div className="col-md-4">
      <label className="form-label">District</label>
      <select className="form-control" id="district" name="district" 
      value={newCoach.district || ""}
      onChange={(e) => setNewCoach({ ...newCoach, district: e.target.value })}
      >
        <option value="">- - Select District - -</option>
        <option value="Chennai">Chennai</option>
        <option value="Madurai">Madurai</option>
      </select>
    </div>
    <div className="col-md-4">
      <label className="form-label">Pincode <small className="text-danger">*</small></label>
      <input type="text" className="form-control" id="pincode" name="pincode" placeholder="Pincode" 
      value={newCoach.pincode || ""}
      onChange={(e) => setNewCoach({ ...newCoach, pincode: e.target.value })}
       />
      <span className="error-message text-danger" id="pincode-errmsg" />
    </div>
  </div>

  {/* Row 6 */}
  <div className="row mb-3">
    <div className="col-md-4">
      <label className="form-label">FIDE ID <small className="text-danger">*</small></label>
      <input type="text" className="form-control" id="fideId" name="fideId" placeholder="FIDE ID" 
      value={newCoach.fideId || ""}
      onChange={(e) => setNewCoach({ ...newCoach, fideId: e.target.value })}
      />
      <span className="error-message text-danger" id="fideId-errmsg" />
    </div>
    <div className="col-md-4">
      <label className="form-label">AICF ID <small className="text-danger">*</small></label>
      <input type="text" className="form-control" id="aicfId" name="aicfId" placeholder="AICF ID" 
      value={newCoach.aicfId || ""}
      onChange={(e) => setNewCoach({ ...newCoach, aicfId: e.target.value })}
      />
      <span className="error-message text-danger" id="aicfId-errmsg" />
    </div>
    <div className="col-md-4">
      <label className="form-label">TNSCA ID <small className="text-danger">*</small></label>
      <input type="text" className="form-control" id="tnscaId" name="tnscaId" placeholder="TNSCA ID"
      value={newCoach.tnscaId || ""}
      onChange={(e) => setNewCoach({ ...newCoach, tnscaId: e.target.value })}
      />
      <span className="error-message text-danger" id="tnscaId-errmsg" />
    </div>
  </div>
</form>

              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-success" onClick={handleAddCoach}>Add</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowAddModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Coach Modal */}
      {showEditModal && selectedCoach && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog" aria-labelledby="exampleModalLabel" aria-hidden="true">
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="exampleModalLabel">Edit Coach</h5>
                <button type="button" className="close" onClick={() => setShowEditModal(false)}>&times;</button>
              </div>
              <div className="modal-body">


      <form className="container">
        {/* Row 1 */}
        <div className="row mb-3">
          <div className="col-md-4">
            <label className="form-label">First Name <small className="text-warning">(Capital Letters Only)*</small></label>
            <input type="text" className="form-control" id="firstname" name="firstname" placeholder="First Name" 
               value={selectedCoach.first_name}
            onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, first_name: e.target.value }))}
                 
              />
            <span className="error-message text-danger" />
          </div>
          <div className="col-md-4">
            <label className="form-label">Middle Name</label>
            <input type="text" className="form-control skip-validation" id="middlename" name="middlename" placeholder="Middle Name" 
             value={selectedCoach.middle_name}
             onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, middle_name: e.target.value }))}
       
            />
            <span className="error-message text-danger" />
          </div>
          <div className="col-md-4">
            <label className="form-label">Last Name <small className="text-warning">(Capital Letters Only)*</small></label>
            <input type="text" className="form-control" id="lastname" name="lastname" placeholder="Last Name"
            value={selectedCoach.last_name}
            onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, last_name: e.target.value }))}
         
                />
            <span className="error-message text-danger" />
          </div>
        </div>
      
        {/* Row 2 */}
        <div className="row mb-3">
          <div className="col-md-4">
            <label className="form-label">Title <small className="text-danger">*</small></label>
            <input type="text" className="form-control" id="title" name="title" placeholder="Title" 
             value={selectedCoach.title}
             onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, title: e.target.value }))}
          
        />
            <span className="error-message text-danger" id="title-errmsg" />
          </div>
          <div className="col-md-4">
            <label className="form-label">Email <small className="text-danger">*</small></label>
            <input type="email" className="form-control" id="email" name="email" placeholder="Email" 
             value={selectedCoach.email}
             onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, email: e.target.value }))}
          
            />
            <span className="error-message text-danger" id="email-errmsg" />
          </div>
          <div className="col-md-4">
            <label className="form-label">Mobile Number <small className="text-danger">*</small></label>
            <input type="text" className="form-control" id="mobile" name="mobile" maxLength={20} placeholder="Mobile Number" 
              value={selectedCoach.mobile_no}
              onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, mobile_no: e.target.value }))}
           
             />
            <span className="error-message text-danger" id="mobile-errmsg" />
          </div>
        </div>
      
        {/* Row 3 */}
        <div className="row mb-3">
          <div className="col-md-4">
            <label className="form-label">Son/Daughter of <small className="text-danger">*</small></label>
            <input type="text" className="form-control" id="parentName" name="parentName" placeholder="Parent's Name"  
             
             value={selectedCoach.father_name}
             onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, father_name: e.target.value }))}
          
             />
            <span className="error-message text-danger" id="parentName-errmsg" />
          </div>
          <div className="col-md-4">
            <label className="form-label">Relationship</label>
            <select className="form-control" id="relationship" name="relationship" 
             value={selectedCoach.relationship}
             onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, relationship: e.target.value }))}
      
            >
              <option value="">- - Select Relationship - -</option>
              <option value="Father">Father</option>
              <option value="Mother">Mother</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Mother Tongue <small className="text-danger">*</small></label>
            <input type="text" className="form-control" id="motherTongue" name="motherTongue" placeholder="Mother Tongue" 
              value={selectedCoach.mother_tongue}
              onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, mother_tongue: e.target.value }))}
        
               />
            <span className="error-message text-danger" id="motherTongue-errmsg" />
          </div>
        </div>
      
        {/* Row 4 */}
        <div className="row mb-3">
          <div className="col-md-4">
            <label className="form-label">Gender</label>
            <select className="form-control" id="gender" name="gender" 
               value={selectedCoach.gender}
               onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, gender: e.target.value }))}
       
               >
              <option value="">- - Select Gender - -</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Date of Birth <small className="text-danger">*</small></label>
            <input type="date" className="form-control" id="dob" name="dob" 
               value={selectedCoach.dob}
               onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, dob: e.target.value }))}
       
               />
            <span className="error-message text-danger" id="dob-errmsg" />
          </div>
          <div className="col-md-4">
            <label className="form-label">Address <small className="text-danger">*</small></label>
            <input type="text" className="form-control" id="address" name="address" placeholder="Address" 
              value={selectedCoach.address}
              onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, address: e.target.value }))}
      
              />
            <span className="error-message text-danger" id="address-errmsg" />
          </div>
        </div>
      
        {/* Row 5 */}
        <div className="row mb-3">
          <div className="col-md-4">
            <label className="form-label">State</label>
            <select className="form-control" id="state" name="state"
        value={selectedCoach.state}
        onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, state: e.target.value }))}
      
            >
              <option value="">- - Select State - -</option>
              <option value="Tamil Nadu">Tamil Nadu</option>
              <option value="Kerala">Kerala</option>
              <option value="Karnataka">Karnataka</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">District</label>
            <select className="form-control" id="district" name="district" 
                value={selectedCoach.district}
                onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, district: e.target.value }))}
        
                >
              <option value="">- - Select District - -</option>
              <option value="Chennai">Chennai</option>
              <option value="Madurai">Madurai</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">Pincode <small className="text-danger">*</small></label>
            <input type="text" className="form-control" id="pincode" name="pincode" placeholder="Pincode" 
                value={selectedCoach.pincode}
                onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, pincode: e.target.value }))}
        
                />
            <span className="error-message text-danger" id="pincode-errmsg" />
          </div>
        </div>
      
        {/* Row 6 */}
        <div className="row mb-3">
          <div className="col-md-4">
            <label className="form-label">FIDE ID <small className="text-danger">*</small></label>
            <input type="text" className="form-control" id="fideId" name="fideId" placeholder="FIDE ID" 
                value={selectedCoach.fide_id}
                onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, fide_id: e.target.value }))}
        
                />
            <span className="error-message text-danger" id="fideId-errmsg" />
          </div>
          <div className="col-md-4">
            <label className="form-label">AICF ID <small className="text-danger">*</small></label>
            <input type="text" className="form-control" id="aicfId" name="aicfId" placeholder="AICF ID" 
               value={selectedCoach.aicf_id}
               onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, aicf_id: e.target.value }))}
       
               />
            <span className="error-message text-danger" id="aicfId-errmsg" />
          </div>
          <div className="col-md-4">
            <label className="form-label">TNSCA ID <small className="text-danger">*</small></label>
            <input type="text" className="form-control" id="tnscaId" name="tnscaId" placeholder="TNSCA ID"
              value={selectedCoach.tnsca_id}
              onChange={(e) => dispatch(setSelectedCoach({ ...selectedCoach, tnsca_id: e.target.value }))}
      
              />
            <span className="error-message text-danger" id="tnscaId-errmsg" />
          </div>
        </div>
      </form>

               
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-info" onClick={handleUpdateCoach}>Update</button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowEditModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Coach Modal */}
     {/* View Coach Modal */}
{showViewModal && selectedCoach && (
  <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1" role="dialog">
    <div className="modal-dialog modal-lg modal-dialog-scrollable">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">View Coach</h5>
          <button type="button" className="close" onClick={() => setShowViewModal(false)}>&times;</button>
        </div>
        <div className="modal-body">

        <table className="table table-bordered table-sm">
            <tbody>
              <tr>
                <th>First Name</th>
                <td>{selectedCoach.first_name}</td>
                <th>Middle Name</th>
                <td>{selectedCoach.middle_name}</td>
                <th>Last Name</th>
                <td>{selectedCoach.last_name}</td>
              </tr>
              <tr>
                <th>Title</th>
                <td>{selectedCoach.title}</td>
                <th>Email</th>
                <td>{selectedCoach.email}</td>
                <th>Mobile No</th>
                <td>{selectedCoach.mobile_no}</td>
              </tr>
              <tr>
                <th>Son/Daughter of</th>
                <td>{selectedCoach.father_name}</td>
                <th>Relationship</th>
                <td>{selectedCoach.relationship}</td>
                <th>Mother Tongue</th>
                <td>{selectedCoach.mother_tongue}</td>
              </tr>
              <tr>
                <th>Gender</th>
                <td>{selectedCoach.gender}</td>
                <th>Date of Birth</th>
                <td>{selectedCoach.dob}</td>
                <th>Address</th>
                <td>{selectedCoach.address}</td>
              </tr>
              <tr>
                <th>State</th>
                <td>{selectedCoach.state}</td>
                <th>District</th>
                <td>{selectedCoach.district}</td>
                <th>Pincode</th>
                <td>{selectedCoach.pincode}</td>
              </tr>
              <tr>
                <th>Fide ID</th>
                <td>{selectedCoach.fide_id}</td>
                <th>AICF ID</th>
                <td>{selectedCoach.aicf_id}</td>
                <th>TNSCA ID</th>
                <td>{selectedCoach.tnsca_id}</td>
              </tr>

            </tbody>
          </table>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={() => setShowViewModal(false)}>
            Close
          </button>
        </div>
      </div>
    </div>
  </div>
)}

    </section>
  );
};

export default Coaches;
